/**
 * MobileCarouselPod Utility Functions
 *
 * This module contains utility functions for the MobileCarouselPod component,
 * including intersection observer thresholds, analytics helpers, and business logic.
 */

// Intersection Observer configuration constants
export const INTERSECTION_CONFIG = {
  IMPRESSION_THRESHOLD: '0.3', // 30% visible for impression tracking
  VIEWPORT_THRESHOLD: '1', // 100% visible for viewport tracking
  ROOT_MARGIN: '0px', // No margin for intersection calculations
  MULTIPLE_OBSERVE: false, // Unobserve after first intersection
};

// Analytics and tracking constants
export const ANALYTICS_CONSTANTS = {
  CAROUSEL_KEY: 'CR_RecommendationCarousel',
  IMPRESSION_EVENT: 'impression',
  CLICK_EVENT: 'click',
  VISIT_EVENT: 'visit',
  CLICK_TO_RATE_ACTION: 'clickToRate',
  AD_TYPE: 'carousel',
};

// Business logic constants
export const BUSINESS_CONSTANTS = {
  MIN_REVIEWS_FOR_DISPLAY: 0, // Minimum reviews to show rating
  MAX_BADGES_TO_DISPLAY: 2, // Maximum number of badges to show
  DEFAULT_SPONSORED_TEXT: 'Patrocinado',
};

// Device-specific behavior constants
export const DEVICE_BEHAVIOR = {
  DESKTOP: {
    BUTTON_VISIBLE_DEFAULT: false,
    HOVER_ENABLED: true,
  },
  MOBILE: {
    BUTTON_VISIBLE_DEFAULT: true,
    HOVER_ENABLED: false,
  },
  TABLET: {
    BUTTON_VISIBLE_DEFAULT: true,
    HOVER_ENABLED: false,
  },
};

/**
 * Determines if the product has more than minimum required reviews
 *
 * @param {number} totalReviews - Total number of reviews
 * @returns {boolean} Whether product has sufficient reviews to display rating
 */
export const hasMinimumReviews = (totalReviews) => {
  return totalReviews && totalReviews > BUSINESS_CONSTANTS.MIN_REVIEWS_FOR_DISPLAY;
};

/**
 * Determines initial button visibility based on device type
 *
 * @param {string} deviceType - Current device type
 * @returns {boolean} Initial button visibility state
 */
export const getInitialButtonVisibility = (deviceType) => {
  const deviceKey = deviceType.toUpperCase();
  const config = DEVICE_BEHAVIOR[deviceKey] || DEVICE_BEHAVIOR.MOBILE;
  return config.BUTTON_VISIBLE_DEFAULT;
};

/**
 * Determines if sponsored pod should be shown based on device and button state
 *
 * @param {boolean} isRecommendedSponsored - Whether product is recommended sponsored
 * @param {boolean} isDesktop - Whether current device is desktop
 * @param {boolean} isMobile - Whether current device is mobile
 * @param {boolean} isButtonVisible - Current button visibility state
 * @returns {boolean} Whether to show sponsored pod
 */
export const shouldShowSponsoredPod = (isRecommendedSponsored, isDesktop, isMobile, isButtonVisible) => {
  if (!isRecommendedSponsored) return false;

  return (isDesktop && !isButtonVisible) || (isMobile && isButtonVisible);
};

/**
 * Determines if impression should be sent for sponsored content
 *
 * @param {boolean} isRecommendedSponsored - Whether product is recommended sponsored
 * @param {boolean} isSponsored - Whether product is sponsored
 * @param {boolean} inViewport - Whether product is in viewport
 * @param {Object} adInfo - Ad information object
 * @returns {boolean} Whether to send impression
 */
export const shouldSendImpression = (isRecommendedSponsored, isSponsored, inViewport, adInfo) => {
  return (isRecommendedSponsored || isSponsored) && inViewport && adInfo;
};

/**
 * Creates analytics event data object for product interactions
 *
 * @param {Object} details - Carousel details object
 * @param {Object} product - Product object
 * @param {Object} appCtx - Application context
 * @param {Function} getTenantValue - Function to get tenant value
 * @returns {Object} Analytics event data
 */
export const createAnalyticsEventData = (details, product, appCtx, getTenantValue) => {
  return {
    carouselName: details.carouselName,
    currentProductID: details.parentVariantID,
    targetProductID: product.offeringId,
    numberProducts: details.numberProducts,
    productPosition: details.productPosition,
    carouselPosition: details.carouselPosition,
    country: getTenantValue(appCtx?.store, appCtx?.regionCode),
    store: appCtx.store,
  };
};

/**
 * Creates Sodimac-specific analytics data for recommendations
 *
 * @param {Object} details - Carousel details object
 * @param {Object} product - Product object
 * @param {Object} appCtx - Application context
 * @param {Function} getTenantValue - Function to get tenant value
 * @returns {Object} Sodimac analytics data
 */
export const createSodimacAnalyticsData = (details, product, appCtx, getTenantValue) => {
  return {
    carouselKey: ANALYTICS_CONSTANTS.CAROUSEL_KEY,
    siteName: getTenantValue(appCtx?.store, appCtx?.regionCode),
    action: ANALYTICS_CONSTANTS.CLICK_TO_RATE_ACTION,
    carouselName: details.carouselName,
    skuId: product.id,
    podPositionInCarousel: details.productPosition,
    lengthPodsInCarousel: details.numberProducts,
    slotId: details.slotId,
  };
};

/**
 * Determines if product is sponsored (either directly or recommended)
 *
 * @param {boolean} isSponsored - Direct sponsored flag
 * @param {Object} product - Product object
 * @returns {boolean} Whether product is sponsored
 */
export const isProductSponsored = (isSponsored, product) => {
  return isSponsored || product?.isSponsored;
};

/**
 * Determines if recommended sponsored content is enabled and product is sponsored
 *
 * @param {boolean} enableSponsoredInRecommendation - Feature toggle
 * @param {Object} product - Product object
 * @returns {boolean} Whether product is recommended sponsored
 */
export const isRecommendedSponsored = (enableSponsoredInRecommendation, product) => {
  return enableSponsoredInRecommendation && product?.isSponsored;
};

/**
 * Limits badges array to maximum display count
 *
 * @param {Array} badges - Array of badges
 * @returns {Array} Limited badges array
 */
export const limitBadgesForDisplay = (badges) => {
  if (!Array.isArray(badges)) return [];
  return badges.slice(0, BUSINESS_CONSTANTS.MAX_BADGES_TO_DISPLAY);
};

/**
 * Gets sponsored text from labels with fallback
 *
 * @param {Object} labels - Labels object
 * @returns {string} Sponsored text
 */
export const getSponsoredText = (labels) => {
  return labels?.SPONSORED_TEXT || BUSINESS_CONSTANTS.DEFAULT_SPONSORED_TEXT;
};
